/**
 * @file LaunchChecklistDialog.tsx
 * @description Окно «Перед открытием магазина» (phase19.28 этап А; PL9):
 * блокирующие условия — красным со ссылкой на раздел, предупреждения —
 * жёлтым. При блокирующих кнопки «Открыть сайт» нет (галочкой их не снять);
 * только предупреждения — «Открыть сайт» доступна. На телефоне — во весь экран.
 */

import { OctagonAlert, TriangleAlert } from "lucide-react";
import { Link } from "react-router";

import { useCurrentContext } from "../../auth/hooks";
import type { LaunchOpenState } from "../../hooks/site-access/useLaunchOpen";
import { launchChecklist } from "../../lib/site-access/launch-check";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { cn } from "@/lib/utils";

const TONE = {
  error: { icon: OctagonAlert, className: "text-[var(--status-error-fg,var(--destructive))]", sr: "Мешает открытию: " },
  warning: { icon: TriangleAlert, className: "text-[var(--status-attention-dot,var(--warning))]", sr: "Предупреждение: " },
} as const;

export function LaunchChecklistDialog({ launch }: { launch: LaunchOpenState }) {
  const slug = useCurrentContext()?.shop?.slug ?? "";
  const check = launch.check;
  const items = check ? launchChecklist(check, `/shop/${slug}`) : [];
  const blocked = !!check?.blocked;

  return (
    <Dialog open={check !== null} onOpenChange={(open) => !open && !launch.busy && launch.close()}>
      <DialogContent fullScreen className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{blocked ? "Магазин пока нельзя открыть" : "Перед открытием магазина"}</DialogTitle>
          <DialogDescription>
            {blocked
              ? "Сначала выполните условия, отмеченные красным: без них сайт не откроется."
              : "Проверьте предупреждения — открыть сайт можно и с ними."}
          </DialogDescription>
        </DialogHeader>
        <ul className="flex flex-col gap-3" aria-label="Чек-лист открытия">
          {items.map((item) => {
            const tone = TONE[item.tone];
            const Icon = tone.icon;
            return (
              <li key={item.key} className="flex items-start gap-2" data-tone={item.tone}>
                <Icon aria-hidden className={cn("mt-0.5 size-5 shrink-0", tone.className)} />
                <span className="type-body min-w-0 flex-1 break-words">
                  <span className="sr-only">{tone.sr}</span>
                  {item.text}
                  {item.to ? (
                    <Link to={item.to} onClick={launch.close} className="mt-1 flex min-h-12 items-center font-medium text-primary hover:underline lg:mt-0 lg:inline-flex lg:min-h-0 lg:pl-1">
                      {item.linkLabel ?? "Открыть раздел →"}
                    </Link>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={launch.close} disabled={launch.busy}>
            {blocked ? "Понятно" : "Отмена"}
          </Button>
          {!blocked ? (
            <Button onClick={() => void launch.confirm()} loading={launch.busy}>
              Открыть сайт
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
